import type { BillingAddonSKUs, BillingAddonsStatusResponse } from '@customerio/hydra-ui-api';
import { hydraUiApi } from '@/services/api';
import { getApiClientErrorStatus, getUserFacingErrorMessage } from '@/services/api/errors';
import { accountStore } from '@/services/auth/store';
import { pollDelay, pollTask } from '@/utils/poll-task';
import { sentryCaptureException } from '@/utils/sentry';
import { isEmptyString } from '@/utils/string';

/** An add-on slug the add-ons API sells, such as `data_driven_segments`. */
export type AddonSku = NonNullable<BillingAddonSKUs['add']>[number];

export type AddonChange = keyof BillingAddonSKUs;

// The run charges the card and waits on Stripe, so give it as long as a wallet purchase.
const ADDON_CHANGE_POLL_MAX_ATTEMPTS = 90;
const ADDON_CHANGE_POLL_INTERVAL_MS = 2000;

export const ADDON_CHANGE_FAILED_MESSAGE = "Your add-on couldn't be added. Try again, or contact support.";
export const ADDON_CHANGE_UNCONFIRMED_MESSAGE =
  "Your add-on is still being added. If it doesn't show up in a few minutes, contact support.";
const ADDON_CHANGE_DECLINED_MESSAGE = 'Your card was declined. Try another card.';
export const ADDON_REMOVAL_FAILED_MESSAGE = "Your add-on couldn't be removed. Try again, or contact support.";
export const ADDON_REMOVAL_UNCONFIRMED_MESSAGE =
  'Your add-on is still being removed. If it still shows in a few minutes, contact support.';

const MESSAGES = {
  add: { failed: ADDON_CHANGE_FAILED_MESSAGE, unconfirmed: ADDON_CHANGE_UNCONFIRMED_MESSAGE },
  remove: { failed: ADDON_REMOVAL_FAILED_MESSAGE, unconfirmed: ADDON_REMOVAL_UNCONFIRMED_MESSAGE },
} satisfies Record<AddonChange, { failed: string; unconfirmed: string }>;

/**
 * How an add-on change ended when it did not succeed. `unknown` is a run that started but whose
 * outcome could not be read.
 */
export type AddonChangeOutcome = 'declined' | 'refused' | 'failed' | 'unknown';

/** An add-on change that ended without the add-on. The message is safe to show the customer. */
export class AddonChangeError extends Error {
  constructor(
    readonly outcome: AddonChangeOutcome,
    message: string,
  ) {
    super(message);
  }
}

/**
 * Starts adding `skus` to, or removing them from, the account's subscription and resolves with the
 * run id. Poll it with {@link pollForAddonChangeTask}. A request the server refuses up front, or one
 * made while another change is still running, throws an {@link AddonChangeError}.
 */
export async function startAddonChange(change: AddonChange, skus: AddonSku[]): Promise<string> {
  const account = accountStore.get();
  if (account == null) {
    throw new Error('Cannot change add-ons without an account');
  }

  try {
    const response = await hydraUiApi.billing.createAddons({
      params: { path: { account_id: String(account.id) } },
      body: { skus: change === 'add' ? { add: skus } : { remove: skus } },
    });
    return response.run_id;
  } catch (error) {
    const status = getApiClientErrorStatus(error);
    if (status === 400 || status === 409) {
      throw new AddonChangeError('refused', getUserFacingErrorMessage(error, MESSAGES[change].failed));
    }
    throw error;
  }
}

/**
 * Polls `billing/addons/status` until the change leaves `pending`. Resolves `true` on success and
 * `undefined` when cancelled. A decline, a refusal, a failure, and a run whose outcome can't be read
 * each throw an {@link AddonChangeError}.
 */
export const pollForAddonChangeTask = pollTask(pollAddonChange);

export function createAddonChangePollTask() {
  return pollTask(pollAddonChange);
}

async function pollAddonChange(signal: AbortSignal, runId: string, change: AddonChange): Promise<boolean> {
  const messages = MESSAGES[change];
  const account = accountStore.get();
  if (account == null) {
    throw new AddonChangeError('unknown', messages.unconfirmed);
  }
  const params = { path: { account_id: String(account.id), run_id: runId } };

  for (let attempt = 0; attempt < ADDON_CHANGE_POLL_MAX_ATTEMPTS; attempt++) {
    if (signal.aborted) {
      return false;
    }

    const response = await readStatus(params, messages.unconfirmed);
    if (response != null && isFinished(response, messages.failed)) {
      return true;
    }
    await pollDelay(ADDON_CHANGE_POLL_INTERVAL_MS, signal);
  }

  sentryCaptureException(
    new Error(`Add-on change run ${runId} did not finish after ${ADDON_CHANGE_POLL_MAX_ATTEMPTS} status reads`),
  );
  throw new AddonChangeError('unknown', messages.unconfirmed);
}

function isFinished(response: BillingAddonsStatusResponse, failedMessage: string): boolean {
  switch (response.status) {
    case 'success':
      return true;
    case 'pending':
      return false;
    case 'declined':
      throw new AddonChangeError('declined', messageOr(response.message, ADDON_CHANGE_DECLINED_MESSAGE));
    case 'refused':
      throw new AddonChangeError('refused', messageOr(response.message, failedMessage));
    case 'failed':
      throw new AddonChangeError('failed', failedMessage);
    default: {
      const exhaustive: never = response.status;
      throw new Error(String(exhaustive));
    }
  }
}

// Services words declined and refused for the customer, but either may come without words.
function messageOr(message: string | null | undefined, fallback: string): string {
  return isEmptyString(message) ? fallback : message;
}

// A 4xx other than 429 will not change, so it ends the poll. The run started, so its outcome is unknown.
async function readStatus(params: { path: { account_id: string; run_id: string } }, unconfirmedMessage: string) {
  try {
    return await hydraUiApi.billing.getAddonsStatus({ params });
  } catch (error) {
    const status = getApiClientErrorStatus(error);
    if (status != null && status >= 400 && status < 500 && status !== 429) {
      sentryCaptureException(error);
      throw new AddonChangeError('unknown', unconfirmedMessage);
    }
    return undefined;
  }
}
